import { useEffect, useRef, useState } from "react";

/** ---------------------- Tipos ---------------------- */
export type Produto = {
  codigo: string;
  ncm: string;
  nome: string;
  classificacao: string;
  posicao: string;
  custo_unitario: number | string | null;
  moeda?: "USD" | "EUR" | "BRL" | string;
};

type ProdutoRaw = Produto & { ocultar?: boolean };

type ListaResp = {
  fonte?: "cache" | "banco";
  versao?: number | null;
  data: Produto[];
};

type AwaitResult = { changed: boolean; versao: number | null };

/** ---------------------- Constantes ---------------------- */
const URL_GET =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-produto";

const URL_AWAIT =
  "https://api.eva-googlemarine.com/departamentos/suprimentos/produtos/await";

const DEPARTAMENTO = "recursos-humanos";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;
const AWAIT_TIMEOUT_MS = 3000;
const AWAIT_GAP_MS = 120;
const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Utils ---------------------- */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function produtosMudaram(a: Produto[], b: Produto[]): boolean {
  if (a.length !== b.length) return true;
  return JSON.stringify(a) !== JSON.stringify(b);
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

/** ---------------------- HTTP helpers ---------------------- */
async function getLista(
  _versaoAtual: number | null,
  signal?: AbortSignal
): Promise<ListaResp> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(URL_GET, {
    method: "POST",
    headers,
    cache: "no-store",
    signal,
    body: JSON.stringify({
      departamento: DEPARTAMENTO,
    }),
  });

  if (!res.ok) {
    throw new Error(`Erro HTTP ${res.status}`);
  }

  const fonte = (res.headers.get("x-source") as "cache" | "banco") || undefined;

  const vh = res.headers.get("x-cache-ver");
  const verHdr = vh ? Number(vh) : null;

  const json = await res.json();
  const verBody = typeof json?.versao === "number" ? json.versao : null;
  const versao = Number.isFinite(verHdr as number) ? verHdr : verBody;

  const visiveisRaw: ProdutoRaw[] | undefined = Array.isArray(json?.visiveis)
    ? (json.visiveis as ProdutoRaw[])
    : undefined;

  const dataRaw: ProdutoRaw[] =
    visiveisRaw ?? (json?.data as ProdutoRaw[]) ?? [];

  const onlyVisible: ProdutoRaw[] =
    visiveisRaw != null
      ? visiveisRaw
      : dataRaw.filter((p) => p?.ocultar !== true);

  const cleanVisible: Produto[] = onlyVisible.map(
    ({ ocultar, ...rest }) => rest
  );

  return {
    fonte,
    versao: versao ?? null,
    data: cleanVisible,
  };
}

async function awaitChange(
  since: number,
  signal?: AbortSignal
): Promise<AwaitResult | null> {
  try {
    const url = `${URL_AWAIT}?since=${since}&timeout_ms=${AWAIT_TIMEOUT_MS}`;

    const token = getAccessToken();
    const headers: Record<string, string> = { Accept: "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(url, {
      method: "GET",
      headers,
      signal,
    });

    if (!res.ok) return null;

    const json = await res.json();
    const versao =
      typeof json?.versao === "number" ? (json.versao as number) : since;
    const changed = Boolean(json?.changed);

    return { changed, versao };
  } catch {
    return null;
  }
}

/** ---------------------- Hook ---------------------- */
export function useProdutosDisponiveis() {
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(true);
  const [fonte, setFonte] = useState<"cache" | "banco" | undefined>(undefined);
  const [versao, _setVersao] = useState<number | null>(null);

  const versaoRef = useRef<number | null>(null);
  const stop = useRef(false);

  const fetchingRef = useRef(false);
  const fetchCtrlRef = useRef<AbortController | null>(null);

  const awaitCtrlRef = useRef<AbortController | null>(null);
  const awaitInFlightRef = useRef(false);

  const setVersao = (v: number | null) => {
    versaoRef.current = v;
    _setVersao(v);
  };

  async function carregarLista(cur: number | null) {
    fetchingRef.current = true;

    const ac = new AbortController();
    fetchCtrlRef.current = ac;

    try {
      const lista = await getLista(cur, ac.signal);

      if (lista.versao != null) {
        setVersao(lista.versao);
      }

      setProdutos((prev) =>
        produtosMudaram(prev, lista.data) ? lista.data : prev
      );

      setFonte(lista.fonte);
    } finally {
      if (fetchCtrlRef.current === ac) {
        fetchCtrlRef.current = null;
      }
      fetchingRef.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    (async () => {
      try {
        await carregarLista(null);
        setLoading(false);

        while (!stop.current) {
          while (fetchingRef.current && !stop.current) {
            await sleep(25);
          }
          if (stop.current) break;

          const cur = versaoRef.current;

          // Se ainda não temos versão, faz polling simples
          if (cur == null) {
            await sleep(POLL_FALLBACK_MS);
            if (stop.current) break;
            await carregarLista(null);
            continue;
          }

          // Long-poll no endpoint await antigo
          if (awaitInFlightRef.current) {
            await sleep(50);
            continue;
          }

          const acAwait = new AbortController();
          awaitCtrlRef.current = acAwait;
          awaitInFlightRef.current = true;

          let resp: AwaitResult | null = null;

          try {
            resp = await awaitChange(cur, acAwait.signal);
          } catch (e: any) {
            if (e?.name !== "AbortError") {
              await sleep(ERROR_BACKOFF_MS);
            }
          } finally {
            if (awaitCtrlRef.current === acAwait) {
              awaitCtrlRef.current = null;
            }
            awaitInFlightRef.current = false;
          }

          if (stop.current) break;

          const avancou = resp ? (resp.versao ?? -1) > cur : false;

          if (resp && (resp.changed || avancou)) {
            await carregarLista(cur);
            await sleep(20);
          } else if (resp === null) {
            // Falha no await → fallback simples
            await sleep(POLL_FALLBACK_MS);
            if (stop.current) break;
            await carregarLista(cur);
          } else {
            await sleep(AWAIT_GAP_MS);
          }
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useProdutosDisponiveis loop error:", e);
        }
        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
      awaitCtrlRef.current?.abort();
      fetchCtrlRef.current?.abort();
    };
  }, []);

  const refresh = async () => {
    while (fetchingRef.current) {
      await sleep(25);
    }
    await carregarLista(versaoRef.current);
  };

  return { produtos, loading, refresh, fonte, versao };
}
